import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ClassIcon from '@mui/icons-material/Class';
import PublicIcon from '@mui/icons-material/Public';
import MenuIcon from '@mui/icons-material/Menu';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Функция для определения активного маршрута
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setAnchorElUser(null);
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleProfileClick = () => {
    setAnchorElUser(null);
    setMobileMenuOpen(false);
    navigate('/user');
  };
  
  const handleNavigation = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const navigationItems = user ? [
    { text: t('navigation.profile'), path: '/user' },
    { text: t('navigation.decks'), path: '/decks' },
    { text: t('navigation.publicDecks'), path: '/public-decks' },
    { text: t('navigation.classes'), path: '/classes' },
  ] : [];

  return (
    <AppBar 
      position="static" 
      sx={{ 
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Гамбургер-меню на мобильных устройствах (только для авторизованных) */}
        {isMobile && user && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Typography 
          variant="h6" 
          component={Link}
          to="/"
          sx={{ 
            fontFamily: '"Noto Serif JP", serif',
            fontWeight: 500,
            textDecoration: 'none',
            color: 'inherit',
            mr: { xs: 1, md: 4 },
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}
        >
          {t('app.title')}
        </Typography>
        
        {/* Горизонтальное меню для десктопов (только для авторизованных) */}
        {!isMobile && user && (
          <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
            {navigationItems.map((item) => (
              <Button 
                key={item.path}
                component={Link} 
                to={item.path} 
                color={isActive(item.path) ? "primary" : "inherit"}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: isActive(item.path) ? 'bold' : 'normal',
                  borderBottom: isActive(item.path) ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                  borderRadius: 0,
                  pb: 0.5
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
          {/* Кнопки авторизации для неавторизованных пользователей */}
          {!user ? (
            <>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => navigate('/signin')}
                sx={{ 
                  whiteSpace: 'nowrap',
                  px: { xs: 1, md: 2 }
                }}
              >
                {isMobile ? t('auth.signInShort') : t('auth.signInTitle')}
              </Button>
            </>
          ) : (
            <>
              {/* Имя пользователя скрываем на мобильных */}
              {!isMobile && (
                <Typography variant="body1">
                  {user.displayName}
                </Typography>
              )}
              
              <IconButton
                size="large"
                edge="end"
                onClick={(event) => setAnchorElUser(event.currentTarget)}
                sx={{ p: 0 }}
              >
                <Avatar 
                  src={user.photoURL || undefined}
                  sx={{ 
                    bgcolor: 'primary.main',
                    width: 32,
                    height: 32,
                  }}
                >
                  {!user.photoURL && (user.displayName?.[0]?.toUpperCase() || 'U')}
                </Avatar>
              </IconButton>
              
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={() => setAnchorElUser(null)}
                onClick={() => setAnchorElUser(null)}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  {t('navigation.profile')}
                </MenuItem>
                <MenuItem onClick={() => {
                  setAnchorElUser(null);
                  navigate('/create-deck');
                }}>
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  {t('decks.createDeck')}
                </MenuItem>
                <MenuItem onClick={() => {
                  setAnchorElUser(null);
                  navigate('/classes/new');
                }}>
                  <ListItemIcon>
                    <ClassIcon fontSize="small" />
                  </ListItemIcon>
                  {t('classes.createClass')}
                </MenuItem>
                <MenuItem onClick={() => {
                  setAnchorElUser(null);
                  navigate('/public-decks');
                }}>
                  <ListItemIcon>
                    <PublicIcon fontSize="small" />
                  </ListItemIcon>
                  {t('decks.publicDecks')}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  {t('auth.logout')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
      
      {/* Боковое drawer-меню для мобильных устройств */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" component="div">
              {user?.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          
          <List>
            {navigationItems.map((item) => (
              <ListItemButton 
                key={item.path} 
                onClick={() => handleNavigation(item.path)}
                selected={isActive(item.path)}
                sx={{
                  bgcolor: isActive(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  color: isActive(item.path) ? 'primary.main' : 'inherit',
                }}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
            
            <Divider sx={{ my: 1 }} />
            
            <ListItemButton onClick={() => handleNavigation('/create-deck')}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('decks.createDeck')} />
            </ListItemButton>
            
            <ListItemButton onClick={() => handleNavigation('/classes/new')}>
              <ListItemIcon>
                <ClassIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('classes.createClass')} />
            </ListItemButton>
            
            <Divider sx={{ my: 1 }} />
            
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('auth.logout')} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header; 